import { useContext, useState } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { Button } from '../Button'
import { Modal } from '../Modal'
import { Text } from '../Text'

import TimezoneContext from 'context/timezoneContext'

import { getLastOrNextConsultations } from 'services/shoppingCart'
import { goToTeleconf } from 'services/teleconf'
import { signTerms } from 'services/user_sign'

import { useUser } from 'providers/UserProvider'

import { Column, Row } from 'components/v2/Grid'



export const AcceptTermsModal = ({ pendingTerm }) => {
  const { user } = useUser()

  const { timezone } = useContext(TimezoneContext)

  const [isModalOpen, setIsModalOpen] = useState(true)

  const { term_id, term_type } = pendingTerm

  const pendingTermContentType = PENDING_TERMS_CONTENT?.[term_type]

  const pendingTermContent = {
    title: pendingTermContentType?.title,
    label: pendingTermContentType?.label,
    description: pendingTermContentType?.[user?.role]?.description,
    url: pendingTermContentType?.[user?.role]?.url,
    signedTermTypeParam: pendingTermContentType?.[user?.role]?.signedTermTypeParam
  }

  const { mutate: onConsentTems } = useMutation({
    mutationFn: () => signTerms({ [pendingTermContent?.signedTermTypeParam]: term_id }),
    onSuccess: data => {
      setIsModalOpen(false)
      toast.success('Termos aceitos com sucesso.')
    },
    onError: error => {
      console.error('Error signing terms:', error)
      toast.error('Houve um erro. Por favor, tente novamente.')
    }
  })

  const { data: availableCvId } = useQuery({
    queryKey: ['currentConsultation', user?.id],
    queryFn: () => getLastOrNextConsultations(user?.id, { timezone }),
    enabled: !!user?.id && user?.role === 'patient',
    select: ({ data }) => {
      const { next } = data?.content

      const availableCvId = next?.[0]?.is_available && next?.[0]?.id

      return availableCvId
    }
  })

  return (
    <Modal isOpened={isModalOpen} shouldCloseOnEscape={false}>
      {() => (
        <Container my='auto' py='24px' px={['16px', '32px']} mx='8px'>
          <Text fontWeight='bold' variant={['regular', 'medium']}>
            {pendingTermContent?.title}
          </Text>
          <Text
            variant={['small', 'regular']}
            dangerouslySetInnerHTML={{ __html: pendingTermContent?.description }}
          />
          <Text variant={['small', 'regular']}>
            Leia{' '}
            <Link href={pendingTermContent?.url || '/'} passHref target='_blank'>
              {`${pendingTermContent?.label}.`}
            </Link>
          </Text>
          <Column gridGap='8px'>
            <Button onClick={onConsentTems} variant='primary' width='100%'>
              <Text variant={['small', 'regular']} fontWeight='400'>
                Li e aceito {pendingTermContent?.label}
              </Text>
            </Button>
            {availableCvId && (
              <Button
                variant='tertiary'
                width='100%'
                onClick={() => goToTeleconf(availableCvId, user?.id)}
              >
                <Text variant={['small', 'regular']}>
                  Seguir para minha consulta e aceitar mais tarde.
                </Text>
              </Button>
            )}
          </Column>
        </Container>
      )}
    </Modal>
  )
}

const PENDING_TERMS_CONTENT = {
  privacy: {
    title: 'Nossa política de privacidade foi atualizada',
    label: 'a política de privacidade',
    patient: {
      signedTermTypeParam: 'signed_privacy_term',
      url: '/privacidade',
      description: `Fizemos ajustes importantes na nossa política de privacidade para reforçar a transparência e a segurança da nossa parceria.
      <br/><br/>
      Para continuar utilizando a plataforma e agendar suas sessões, é necessário aceitar a nova política de privacidade.`
    },
    professional: {
      signedTermTypeParam: 'signed_privacy_term',
      url: '/privacidade',
      description: `Fizemos ajustes importantes na nossa política de privacidade para reforçar a transparência e a segurança da nossa parceria.
        <br/><br/>
        Para continuar atendendo normalmente pela plataforma, é necessário aceitar a nova política. Na ausência do aceite, não será possível realizar ou receber novos agendamentos.`
    }
  },
  usage: {
    title: 'Nossos termos de uso foram atualizados',
    label: 'os termos de uso',
    patient: {
      signedTermTypeParam: 'signed_patient_term',
      url: '/termos-paciente',
      description: `Fizemos ajustes importantes em nossos termos de uso para reforçar a transparência e a segurança da nossa parceria. 
      <br/><br/>
      Para continuar utilizando a plataforma e agendar suas sessões, é necessário aceitar os novos termos de uso.`
    },
    professional: {
      signedTermTypeParam: 'signed_professional_term',
      url: '/termos-psicologos',
      description: `Fizemos ajustes importantes em nossos termos de uso para reforçar a transparência e a segurança da nossa parceria.
      <br/><br/>
      Para continuar atendendo normalmente pela plataforma, é necessário aceitar os novos termos de uso. Na ausência do aceite, não será possível realizar ou receber novos agendamentos.`
    }
  }
}

const Container = styled(Column)`
  max-width: 640px;
  justify-content: space-between;
  align-self: center;
  width: 100%;
  height: fit-content;
  border-radius: 4px;
  gap: 24px;
  background: white;
  text-align: left;
`
